import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { DocumentLibraryService } from '@app/core/services/document-library.service';
import { LoanInfoService } from '@app/core/services/loan-info.service';
import { ProposalService } from '@app/core/services/proposal.service';
import { SpinnerStateService } from '@app/core/services/spinner-state.service';
import { UnityDirectMegaMenuService } from '@app/core/services/unity-direct-mega-menu.service';
import { UnityDirectRatesService } from '@app/core/services/unity-direct-rates.service';
import { UnityDirectStateService } from '@app/core/services/unity-direct-state.service';
import { IDocLibrary } from '@app/models/document.model';
import { LoanStatus } from '@app/models/Loan-Status';
import { getEmptyNadaAdvancedValueRequest } from '@app/models/nada-vehicle-response.model';
import { Pages } from '@app/models/Pages';
import { QuoteSearchResult } from '@app/models/quote-search.model';
import { IRecentActivityResponse, Steps } from '@app/models/unity-direct.model';
import { loanStatusText } from '@app/shared/constants/loanStatusText';
import * as moment from 'moment';
import { fromEvent, merge, Subject } from 'rxjs';
import { auditTime, filter, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-unity-direct-header-bar',
  templateUrl: './unity-direct-header-bar.component.html',
  styleUrls: ['./unity-direct-header-bar.component.scss']
})
export class UnityDirectHeaderBarComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  isShowMegaMenu: boolean;
  isQuotesMegaMenu: boolean;
  isBillingPage: boolean;
  isAdminPage: boolean;
  searchQuoteForm: UntypedFormGroup;
  searchResultReturned: boolean = false;
  isResourcesMegaMenu: boolean;
  isContentLoading: boolean = false;
  isResultLoading: boolean = false;
  docStart: number = 0;
  docEnd: number = 0;
  listenerFn: () => void;
  @ViewChild('udHeader') udHeader: ElementRef;
  onDestroy$: Subject<boolean> = new Subject<boolean>();
  onMegaMenuDestroy$: Subject<boolean> = new Subject<boolean>();
  quoteActivityArr: IRecentActivityResponse[] = [];
  searchResult: QuoteSearchResult[] = [];
  searchError: boolean = false;
  documents: any = [];
  loanStatus: string;
  isNewQuote: boolean = true;
  canUserAccessNewQuote: boolean = false;
  canUserAccessBilling: boolean = false;
  isShowAdmin: boolean = false;
  private _megaMenuElement?: HTMLElement;

  @ViewChild('megaMenu')
  set megaMenuRef(megaMenuRef: ElementRef<HTMLElement>) {
    this._megaMenuElement = megaMenuRef?.nativeElement;
    if (this.isShowMegaMenu && this._megaMenuElement) {
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => this.positionMegaMenu());
      });
    }
  }

  constructor(
    @Inject(UnityDirectStateService) private readonly udStateService,
    @Inject(UnityDirectMegaMenuService) private readonly udMegaMenuService,
    private readonly renderer: Renderer2,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly documentService: DocumentLibraryService,
    private readonly proposalService: ProposalService,
    private readonly router: Router,
    private readonly udRateService: UnityDirectRatesService,
    private readonly spinnerStateService: SpinnerStateService,
    private readonly zone: NgZone,
    public loanInfo: LoanInfoService
  ) {}

  ngOnInit(): void {
    this.isShowAdmin =
      this.udStateService.isAdminUser &&
      this.udStateService.canUserAccessAndModifyAdminClientApp;
    this.getLoanStatus();
    this.makeSearchForm();
    this.canUserAccessNewQuote = this.udStateService.canUserAccessNewQuote;
    this.canUserAccessBilling = this.udStateService.canUserAccessBilling;
    this.udStateService.openBillingBehaviorSub$
      .pipe(
        take(1),
        tap((isOpenBilling: boolean) => isOpenBilling && this.openBillingPage())
      )
      .subscribe();
  }

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => {
      merge(
        fromEvent(globalThis, 'scroll', { passive: true }),
        fromEvent(globalThis, 'resize')
      )
        .pipe(auditTime(16), takeUntil(this.onDestroy$))
        .subscribe(() => {
          if (this.isShowMegaMenu) {
            this.positionMegaMenu();
          }
        });
    });
  }

  get isShowEstimatedMonthlyPayment(): boolean {
    return this.udStateService.isShowEstimatedMonthlyPayment;
  }

  getMonthlyPaymentValue(): number {
    return this.udStateService.monthlyPayment || 0;
  }

  /**
   * Returns Loan status
   */
  getLoanStatus(): void {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter(e => e instanceof NavigationEnd)
      )
      .subscribe((e: NavigationEnd) => {
        if (e.url.includes(Pages.Review)) {
          this.isNewQuote = false;
          this.isBillingPage = false;
          this.isAdminPage = false;
        }
        const lStatus =
          this.router.getCurrentNavigation()?.extras.state?.status ||
          LoanStatus.New;
        this.loanStatus = this.getTextforLoanStatus(lStatus);
      });

    this.udStateService.loanStatusSubject
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(status => {
        this.loanStatus = this.getTextforLoanStatus(status);
        this.udStateService.loanStatus = this.loanStatus;
        this.isNewQuote = this.loanStatus === LoanStatus.New;
      });
  }

  /**
   * Creates Header Search form.
   */
  makeSearchForm(): void {
    this.searchQuoteForm = this._formBuilder.group({
      lastName: [''],
      firstName: [''],
      email: [''],
      refNumber: [''],
      accountNumber: [''],
      vin: [''],
      swbcRefNum: ['']
    });
  }

  /**
   * Watch quote search form for changes and reset results.
   */
  subscribeToQuoteSearchForm(): void {
    this.searchQuoteForm.valueChanges
      .pipe(
        tap(() => {
          this.searchResult = [];
          this.searchResultReturned = false;
        }),
        takeUntil(this.onMegaMenuDestroy$)
      )
      .subscribe();
  }

  /**
   * Opens the mega menu
   * @param menuItem {string}
   */
  openMegaMenu(menuItem: string): void {
    this.searchResult = this.udMegaMenuService.quoteSearchResultStore;
    this.searchQuoteForm.patchValue(
      this.udMegaMenuService.searchFormStore || this.searchQuoteForm.value,
      { onlySelf: true, emitEvent: false }
    );
    this.subscribeToQuoteSearchForm();
    this.searchResultReturned = Boolean(this.searchResult.length);
    queueMicrotask(() => this.positionMegaMenu());
    this.isShowMegaMenu = true;
    switch (menuItem) {
      case 'newQuote':
      case 'quotes':
        this.isQuotesMegaMenu = true;
        this.isResourcesMegaMenu = false;
        this.loadQuotesActivity();
        break;
      case 'billing':
        this.isQuotesMegaMenu = false;
        this.isResourcesMegaMenu = false;
        break;
      case 'resources':
        this.isQuotesMegaMenu = false;
        this.loadDocuments();
    }

    // Set up a listener to detect clicks outside the megamenu and header and close it if clicked outside
    // We don't want this listener to always be listening for random clicks. Instead listen only when megamenu is open.
    this.listenerFn = this.renderer.listen(
      'window',
      'click',
      (event: Event) => {
        if (
          event.target !== this.udHeader.nativeElement &&
          !this.udHeader.nativeElement.contains(event.target) &&
          event.target !== this._megaMenuElement &&
          !this._megaMenuElement.contains(event.target as Node)
        ) {
          this.closeMegaMenu();
        }
      }
    );
  }

  positionMegaMenu(): void {
    const headerEl = this.udHeader.nativeElement;
    const menuEl = this._megaMenuElement;
    if (!menuEl) return;

    const headerRect = headerEl.getBoundingClientRect();
    const topPx = Math.round(headerRect.bottom);
    this.renderer.setStyle(menuEl, 'top', `${topPx}px`);
  }

  /**
   * When New quote button is clicked from either Direct worksheet or Review, reset state and navigate to worksheet.
   */
  openNewQuote(): void {
    sessionStorage.removeItem('valueType');
    sessionStorage.removeItem('optionalEquipment');
    this.closeMegaMenu();
    this.spinnerStateService.show();
    this.router.navigate(['/']);
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.udStateService.resetState();
    this.udStateService.isProductsRatesAvailableSubject.next(false);
    this.udStateService.currentStep.next(Steps['BorrowerInfo']);
    this.udStateService.isResetUDFormSubject.next(true);
    this.udStateService.loanStatusSubject.next(LoanStatus.New);
    this.isNewQuote = true;
    this.isShowMegaMenu = false;
    this.isBillingPage = false;
    this.isAdminPage = false;
    this.udStateService.isVehicleValueReturned = false;
    this.proposalService.nadaAdvancedValueRequest =
      getEmptyNadaAdvancedValueRequest();
    this.udRateService.resetPenTrackKeys();
    setTimeout(() => {
      // This is only a cosmetic delay to let the user know that something has changed.
      this.spinnerStateService.hide();
    }, 1000);
  }

  /**
   * When user clicks on "Billing" in the header menu, open Billing page
   */
  openBillingPage(): void {
    this.closeMegaMenu();
    this.spinnerStateService.show();
    this.router.navigate([Pages.Billing]);
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
    this.isNewQuote = false;
    this.isShowMegaMenu = false;
    this.isBillingPage = true;
    this.isAdminPage = false;
    setTimeout(() => {
      // This is only a cosmetic delay to let the user know that something has changed.
      this.spinnerStateService.hide();
    }, 1000);
  }

  openAdminPage(): void {
    this.closeMegaMenu();
    this.router.navigate([Pages.Admin]);
    this.isNewQuote = false;
    this.isShowMegaMenu = false;
    this.isBillingPage = false;
    this.isAdminPage = true;
  }

  /**
   * Method to close Mega Menu
   */
  closeMegaMenu(): void {
    this.isQuotesMegaMenu = false;
    this.isResourcesMegaMenu = false;
    this.isShowMegaMenu = false;

    // Cancel the megamenu event listener
    if (this.listenerFn) {
      this.listenerFn();
    }

    this.onMegaMenuDestroy$.next();
    this.onMegaMenuDestroy$.complete();
  }

  /**
   * Loads Recent Activity in Mega menu
   */
  loadQuotesActivity(): void {
    this.isContentLoading = true;
    this.udMegaMenuService
      .getRecentActivity()
      .pipe(take(1))
      .subscribe((recentActivityData: IRecentActivityResponse[]) => {
        recentActivityData.forEach((activity: IRecentActivityResponse) => {
          activity.productSoldDate = moment(activity.productSoldDate).format(
            'MM/DD/YYYY'
          );
        });
        this.quoteActivityArr = recentActivityData;
        this.isContentLoading = false;
      });
  }

  /**
   * Loads documents/resources
   */
  loadDocuments(): void {
    if (!this.documents.length) {
      this.isResourcesMegaMenu = true;
      this.isContentLoading = true;
      this.documentService
        .getDocuments(this.udStateService.userInfo.AccountId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((res: IDocLibrary) => {
          this.documents = res;
          this.isContentLoading = false;
          if (this.documents.length >= 12) {
            this.docEnd = 12;
          } else {
            this.docEnd = this.documents.length;
          }
        });
    } else if (!this.isResourcesMegaMenu) {
      this.isResourcesMegaMenu = true;
      this.docStart = 0;
      this.docEnd = 12;
    }
  }

  /**
   * Resources section anv
   */
  showPrevDocs(): void {
    this.docStart -= 12;
    this.docEnd = this.docStart + 12;
  }

  /**
   * Search quote results on a button click
   */
  searchQuote(): void {
    this.searchResult = [];
    this.searchResultReturned = false;
    this.isResultLoading = true;
    const params = {
      lastName: this.searchQuoteForm.value.lastName || '',
      firstName: this.searchQuoteForm.value.firstName || '',
      emailAddress: this.searchQuoteForm.value.email || '',
      vin: this.searchQuoteForm.value.vin || '',
      accountNumber: this.searchQuoteForm.value.accountNumber || '',
      swbcRefNum: this.searchQuoteForm.value.swbcRefNum || 0
    };
    this.udMegaMenuService
      .getSearchData(params)
      .pipe(take(1))
      .subscribe({
        next: (data: QuoteSearchResult[]) => {
          this.searchResult = data;
          this.searchResultReturned = true;
          this.isResultLoading = false;
        },
        error: () => {
          this.searchError = true;
          this.isResultLoading = false;
        }
      });
  }

  /**
   * Navigate to Review page from UD for Recent Activity list
   * @param id {number} qoute loan id
   * @param status {LoanStatus}
   */
  navigateToReview(id: number, status: LoanStatus): void {
    this.closeMegaMenu();
    this.router.navigate([Pages.Review], { state: { id, status } });
  }

  /**
   * Navigate to Review page from UD for Search Results
   * @param id {number} quoteLoan Id
   * @param loanStatus {number}
   */
  navigateToReviewSearchResults(id: number, loanStatus: number): void {
    let status: LoanStatus;
    switch (loanStatus) {
      case 0:
        status = LoanStatus.Proposal;
        break;
      case 1:
        status = LoanStatus.Booking;
        break;
      default:
        status = LoanStatus.Void;
    }
    this.closeMegaMenu();
    this.router.navigate([Pages.Review], { state: { id, status } });
  }

  /**
   * To get Loan status as a text for display
   * @param loanStatus {string}
   * @returns {string}
   */
  getTextforLoanStatus(loanStatus: string): string {
    return loanStatusText[loanStatus];
  }

  /**
   * Resources section nav
   */
  showNextDocs(): void {
    this.docStart += 12;
    if (this.docStart + 12 > this.documents.length) {
      this.docEnd = this.documents.length;
    } else {
      this.docEnd = this.docStart + 12;
    }
  }

  ngOnDestroy(): void {
    if (this.listenerFn) {
      this.listenerFn();
    }
    this.onMegaMenuDestroy$.next();
    this.onMegaMenuDestroy$.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
